import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import RegisterEmployee from "./pages/RegisterEmployee";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import WithdrawRequest from "./pages/WithdrawRequest";
import MyDataPage from "./pages/MyDataPage";
import SubAdminsPage from "./pages/SubAdminsPage";
import AuditLogPage from "./pages/AuditLogPage";
import NotificationsPage from "./pages/NotificationsPage";
import StatsPage from "./pages/StatsPage";
import PendingRequestsPage from "./pages/PendingRequestsPage";
import CookieBanner from "./components/CookieBanner";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import OnboardingPlan from "./pages/OnboardingPlan";
import AppLayout from "./pages/AppLayout";
import Dashboard from "./pages/Dashboard";
import CalendarPage from "./pages/CalendarPage";
import CheckinPage from "./pages/CheckinPage";
import ExpensesPage from "./pages/ExpensesPage";
import DocumentsPage from "./pages/DocumentsPage";
import PayrollPage from "./pages/PayrollPage";
import AIAnalyticsPage from "./pages/AIAnalyticsPage";
import BillingPage from "./pages/BillingPage";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import EmployeesPage from "./pages/EmployeesPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import LeavesPage from "./pages/LeavesPage";
import OrdersPage from "./pages/OrdersPage";
import ReportsPage from "./pages/ReportsPage";
import PayslipsPage from "./pages/PayslipsPage";
import TicketsPage from "./pages/TicketsPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import TimesheetPage from "./pages/TimesheetPage";
import ShiftsPage from "./pages/ShiftsPage";
import SignDocumentPage from "./pages/SignDocumentPage";
import ManualPage from "./pages/ManualPage";
import ChatPage from "./pages/ChatPage";
import AttendanceLogPage from "./pages/AttendanceLogPage";
import AdvancedFeaturesPage from "./pages/AdvancedFeaturesPage";
import EmployeeAdvancedPage from "./pages/EmployeeAdvancedPage";
import WhistleblowingPublic from "./pages/WhistleblowingPublic";
import EmployeeCVPage from "./pages/EmployeeCVPage";
import AdminCVPage from "./pages/AdminCVPage";
import "./lib/i18n";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-employee" element={<RegisterEmployee />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/withdraw-request" element={<WithdrawRequest />} />
          <Route path="/whistleblowing" element={<WhistleblowingPublic />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding/plan" element={<Protected><OnboardingPlan /></Protected>} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/portal" element={<Protected><EmployeePortalPage /></Protected>} />
          <Route path="/portal/advanced" element={<Protected><EmployeeAdvancedPage /></Protected>} />
          <Route path="/portal/cv" element={<Protected><EmployeeCVPage /></Protected>} />
          <Route path="/sign/:rid" element={<SignDocumentPage />} />
          <Route path="/app" element={<Protected><AppLayout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="checkin" element={<CheckinPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="payroll" element={<PayrollPage />} />
            <Route path="ai" element={<AIAnalyticsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="company" element={<CompanyProfilePage />} />
            <Route path="leaves" element={<LeavesPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="payslips" element={<PayslipsPage />} />
            <Route path="timesheet" element={<TimesheetPage />} />
            <Route path="shifts" element={<ShiftsPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="my-data" element={<MyDataPage />} />
            <Route path="sub-admins" element={<SubAdminsPage />} />
            <Route path="pending-requests" element={<PendingRequestsPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="stats" element={<StatsPage />} />
            <Route path="manual" element={<ManualPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="attendance-log" element={<AttendanceLogPage />} />
            <Route path="advanced" element={<AdvancedFeaturesPage />} />
            <Route path="cv" element={<AdminCVPage />} />
            <Route path="admin" element={<SuperAdminPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
